import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Card, CardContent, TextField,
  MenuItem, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Stack, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SecurityIcon from "@mui/icons-material/Security";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNullablePickerContext } from "@mui/x-date-pickers/internals";
import { useForkRef } from "@mui/material/utils";
import api from "../../../services/api";

// ─── helpers ──────────────────────────────────────────────────────────────────

// Custom Field for DatePicker (Versi Paling Aman & Kompatibel)
function ButtonField(props) {
  const { label, id, disabled, ref: inputRef } = props;
  const pickerContext = useNullablePickerContext();
  const handleRef = useForkRef(pickerContext?.triggerRef, inputRef);
  
  // Deteksi apakah sudah ada tanggal yang dipilih
  const hasValue = pickerContext?.value != null;
  const displayLabel = pickerContext?.value 
    ? dayjs(pickerContext.value).format("DD MMM YYYY") 
    : label;

  return (
    <Button
      variant="outlined"
      id={id}
      disabled={disabled}
      ref={handleRef}
      size="small"
      startIcon={<CalendarTodayRoundedIcon sx={{ fontSize: "13px !important" }} />}
      onClick={() => pickerContext?.setOpen((prev) => !prev)}
      sx={{
        borderRadius: "10px", fontSize: "0.78rem", textTransform: "none",
        fontWeight: 600, minWidth: "fit-content", height: 40, px: 2,
        borderColor: hasValue ? "#2563eb" : "#e5e7eb",
        color: hasValue ? "#2563eb" : "#374151",
        bgcolor: hasValue ? "#eff6ff" : "#f9fafb",
        "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff" },
      }}
    >
      {displayLabel || "Pilih Tanggal"}
    </Button>
  );
}

const ACTION_CFG = {
  created:  { label: "Dibuat",    bg: "#f0fdf4", color: "#166534", border: "#bbf7d0", dot: "#16a34a" },
  updated:  { label: "Diubah",    bg: "#fefce8", color: "#854d0e", border: "#fde68a", dot: "#d97706" },
  deleted:  { label: "Dihapus",   bg: "#fef2f2", color: "#991b1b", border: "#fecaca", dot: "#dc2626" },
  approved: { label: "Disetujui", bg: "#f0fdf4", color: "#166534", border: "#bbf7d0", dot: "#16a34a" },
  rejected: { label: "Ditolak",   bg: "#fef2f2", color: "#991b1b", border: "#fecaca", dot: "#dc2626" },
  imported: { label: "Diimpor",   bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe", dot: "#3b82f6" },
  login:    { label: "Login",     bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe", dot: "#3b82f6" },
  logout:   { label: "Logout",    bg: "#f9fafb", color: "#4b5563", border: "#e5e7eb", dot: "#6b7280" },
};

const MODEL_OPTIONS = [
  { value: "",                   label: "Semua" },
  { value: "ProcurementRequest", label: "Pengajuan" },
  { value: "Item",               label: "Barang" },
  { value: "ItemMovement",       label: "Pergerakan Barang" },
  { value: "Autentikasi",        label: "Autentikasi" },
  { value: "User",               label: "User" },
  { value: "Room",               label: "Ruangan" },
];

const formatDate = (d) =>
  new Date(d).toLocaleString("id-ID", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

// ─── action badge ──────────────────────────────────────────────────────────────

function ActionBadge({ action }) {
  const c = ACTION_CFG[action] || { label: action, bg: "#f9fafb", color: "#374151", border: "#e5e7eb" };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center",
      px: "9px", py: "2px", borderRadius: "20px",
      fontSize: "0.68rem", fontWeight: 600,
      bgcolor: c.bg, color: c.color,
      border: `0.5px solid ${c.border}`,
    }}>
      {c.label}
    </Box>
  );
}

// ─── timeline dot ─────────────────────────────────────────────────────────────

function TimelineDot({ action }) {
  const c = ACTION_CFG[action];
  return (
    <Box sx={{
      width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
      bgcolor: c?.dot || "#9ca3af",
    }} />
  );
}

// ─── diff viewer ──────────────────────────────────────────────────────────────

function DiffViewer({ oldValues, newValues }) {
  if (!oldValues && !newValues)
    return (
      <Typography sx={{ fontSize: "0.82rem", color: "#9ca3af" }}>
        Tidak ada detail perubahan.
      </Typography>
    );

  const keys = Array.from(new Set([
    ...Object.keys(oldValues || {}),
    ...Object.keys(newValues || {}),
  ]));

  return (
    <TableContainer sx={{ border: "0.5px solid #f3f4f6", borderRadius: "10px", overflow: "hidden", mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f9fafb" }}>
            {[
              { label: "Field",    color: "#9ca3af", w: "30%" },
              { label: "Sebelum", color: "#991b1b" },
              { label: "Sesudah", color: "#166534" },
            ].map((h) => (
              <TableCell key={h.label} sx={{
                fontSize: "0.65rem", fontWeight: 700, color: h.color,
                textTransform: "uppercase", letterSpacing: "0.06em",
                borderBottom: "0.5px solid #f3f4f6", width: h.w,
              }}>
                {h.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.map((k) => (
            <TableRow key={k} sx={{ "& td": { border: "none", borderBottom: "0.5px solid #f9fafb" } }}>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#6b7280" }}>{k}</TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#b91c1c" }}>
                {oldValues?.[k] !== undefined ? String(oldValues[k]) : "—"}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem", color: "#15803d" }}>
                {newValues?.[k] !== undefined ? String(newValues[k]) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── main ──────────────────────────────────────────────────────────────────────

export default function LogAktivitas() {
  const [filters, setFilters] = useState({
    search: "", action: "", model_type: "", date_from: "", date_to: "",
  });
  const [logs, setLogs]       = useState(null);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);
  const [detail, setDetail]   = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/activity-logs", { params: { ...filters, page, per_page: 20 } }),
        api.get("/activity-logs/stats"),
      ]);
      setLogs(r1.data);
      setStats(r2.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => { setPage(1); fetchLogs(); };

  const handleExportCSV = () => {
    if (!logs?.data?.length) return;
    
    // ─── Trik HTML to Excel (biar rapi ada warna & border) ───
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <style>
          .title { font-size: 16pt; font-weight: bold; text-align: center; color: #111827; }
          .th { background-color: #f1f5f9; font-weight: bold; text-align: center; border: 0.5px solid #cbd5e1; }
          .td { border: 0.5px solid #e2e8f0; padding: 4px; font-size: 10pt; }
          .action-badge { font-weight: bold; text-transform: uppercase; font-size: 8pt; }
        </style>
      </head>
      <body>
        <table>
          <tr><td colspan="7" class="title">LOG AKTIVITAS SISTEM SARPRAS</td></tr>
          <tr><td colspan="7" style="text-align: center;">BAS-LPKIA · Tanggal Ekspor: ${new Date().toLocaleString('id-ID')}</td></tr>
          <tr><td colspan="7"></td></tr>
          <tr>
            <td class="th">Waktu</td>
            <td class="th">User</td>
            <td class="th">Aksi</td>
            <td class="th">Modul</td>
            <td class="th">Label/ID</td>
            <td class="th">IP Address</td>
            <td class="th">User Agent</td>
          </tr>
    `;

    logs.data.forEach((l) => {
      html += `
        <tr>
          <td class="td">${formatDate(l.created_at)}</td>
          <td class="td">${l.user?.name || "—"}</td>
          <td class="td" style="text-align: center;">${l.action.toUpperCase()}</td>
          <td class="td">${l.model_name || l.model_type}</td>
          <td class="td">${l.model_label || "—"}</td>
          <td class="td" style="font-family: monospace;">${l.ip_address || "—"}</td>
          <td class="td" style="font-size: 8pt; color: #6b7280;">${l.user_agent || "—"}</td>
        </tr>
      `;
    });

    html += `
        </table>
      </body>
      </html>
    `;

    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Log_Aktivitas_${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── shared sx ────────────────────────────────────────────────────────────
  const cardSx = {
    elevation: 0,
    sx: { border: "0.5px solid #f3f4f6", borderRadius: "14px" },
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "9px", fontSize: "0.82rem", bgcolor: "#f9fafb", height: "34px",
      "& fieldset": { borderColor: "#e5e7eb", borderWidth: "0.5px" },
      "&:hover fieldset": { borderColor: "#d1d5db" },
      "&.Mui-focused fieldset": { borderColor: "#9ca3af", borderWidth: "1px" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.82rem" },
    "& .MuiInputBase-input": { py: "6px", px: "10px" },
    "& .MuiSelect-select": { py: "6px !important" },
  };

  const labelSx = {
    fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af",
    textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5,
  };

  const thSx = {
    fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af",
    textTransform: "uppercase", letterSpacing: "0.06em",
    py: 1.2, borderBottom: "0.5px solid #f3f4f6", bgcolor: "#f9fafb",
  };

  const tdSx = {
    border: "none", borderBottom: "0.5px solid #f9fafb",
    fontSize: "0.82rem", py: 1.1,
  };

  return (
    <Box p={3}>
      {/* ── Page Header ────────────────────────────────────────────────── */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <Box>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.04em", lineHeight: 1 }}>
            Log Aktivitas
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#9ca3af", mt: 0.8, fontWeight: 500 }}>
            Pantau setiap pergerakan dan perubahan data dalam sistem secara real-time.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={handleExportCSV}
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: "0.8rem",
              borderColor: "#e5e7eb", color: "#374151", height: 40, px: 2,
              "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff", color: "#2563eb" }
            }}
          >
            Export Log
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
            onClick={fetchLogs}
            sx={{
              borderRadius: "10px", textTransform: "none", fontWeight: 700, fontSize: "0.8rem",
              bgcolor: "#111827", height: 40, px: 2, boxShadow: "none",
              "&:hover": { bgcolor: "#1f2937", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* ── Stats Center ───────────────────────────────────────────────── */}
      {stats && (
        <Grid container spacing={2} mb={4}>
          {[
            { label: "Aktivitas Hari Ini", value: (stats.today?.created || 0) + (stats.today?.updated || 0) + (stats.today?.deleted || 0), icon: <AssessmentIcon sx={{ fontSize: 20 }} />, color: "#2563eb", bg: "#eff6ff" },
            { label: "Data Dibuat", value: stats.today?.created || 0, icon: <AddCircleOutlineIcon sx={{ fontSize: 20 }} />, color: "#059669", bg: "#ecfdf5" },
            { label: "Data Diubah", value: stats.today?.updated || 0, icon: <EditNoteIcon sx={{ fontSize: 22 }} />, color: "#d97706", bg: "#fffbeb" },
            { label: "Total Jejak Audit", value: stats.total || 0, icon: <SecurityIcon sx={{ fontSize: 20 }} />, color: "#7c3aed", bg: "#f5f3ff" },
          ].map((s, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Box sx={{
                p: 2.5, bgcolor: "#fff", borderRadius: "16px", border: "1px solid #f3f4f6",
                display: "flex", alignItems: "center", gap: 2,
                transition: "transform 0.2s", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }
              }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: "12px", bgcolor: s.bg,
                  display: "flex", alignItems: "center", justifyContent: "center", color: s.color
                }}>
                  {s.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", fontWeight: 600, mt: 0.3 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Filter Bar ─────────────────────────────────────────────────── */}
      <Box sx={{
        mb: 4, p: 1.5, bgcolor: "#fff", borderRadius: "16px", border: "1px solid #f3f4f6",
        display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center"
      }}>
        <TextField
          placeholder="Cari user, label, atau modul..."
          variant="outlined" size="small"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, color: "#9ca3af", mr: 1 }} />,
            sx: { borderRadius: "10px", bgcolor: "#f9fafb", fontSize: "0.82rem" }
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            select size="small" value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.82rem" } }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">Semua Aksi</MenuItem>
            {Object.entries(ACTION_CFG).map(([k, v]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: "0.82rem" }}>{v.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select size="small" value={filters.model_type}
            onChange={(e) => setFilters({ ...filters, model_type: e.target.value })}
            sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.82rem" } }}
            SelectProps={{ displayEmpty: true }}
          >
            {MODEL_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.82rem" }}>{o.label || "Semua Modul"}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={filters.date_from ? dayjs(filters.date_from) : null}
              label={filters.date_from ? dayjs(filters.date_from).format("DD MMM YYYY") : "Dari Tanggal"}
              onChange={(v) => setFilters({ ...filters, date_from: v?.isValid() ? v.format("YYYY-MM-DD") : "" })}
              slots={{ field: ButtonField }}
            />
          </LocalizationProvider>
          
          <ArrowRightAltIcon sx={{ fontSize: 18, color: "#d1d5db" }} />
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={filters.date_to ? dayjs(filters.date_to) : null}
              label={filters.date_to ? dayjs(filters.date_to).format("DD MMM YYYY") : "Sampai Tanggal"}
              onChange={(v) => setFilters({ ...filters, date_to: v?.isValid() ? v.format("YYYY-MM-DD") : "" })}
              slots={{ field: ButtonField }}
              minDate={filters.date_from ? dayjs(filters.date_from) : undefined}
            />
          </LocalizationProvider>
        </Box>

        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{
            borderRadius: "10px", bgcolor: "#2563eb", boxShadow: "none",
            height: 40, px: 3, textTransform: "none", fontWeight: 700,
            "&:hover": { bgcolor: "#1d4ed8" }
          }}
        >
          Terapkan
        </Button>
      </Box>

      {/* ── Timeline Feed ────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {loading ? (
          <Box textAlign="center" py={8}><CircularProgress size={28} /></Box>
        ) : logs?.data?.length === 0 ? (
          <Card {...cardSx} sx={{ ...cardSx.sx, p: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: "0.85rem" }}>Tidak ada aktivitas ditemukan</Typography>
          </Card>
        ) : (
          logs?.data?.map((log, idx) => {
            const cfg = ACTION_CFG[log.action] || { dot: "#9ca3af", color: "#374151", bg: "#f9fafb" };
            return (
              <Box key={log.id} sx={{ display: "flex", gap: 2, position: "relative" }}>
                {/* Garis Timeline Vertical */}
                {idx !== logs.data.length - 1 && (
                  <Box sx={{
                    position: "absolute", left: 19, top: 40, bottom: -16,
                    width: "1.5px", bgcolor: "#f3f4f6", zIndex: 0
                  }} />
                )}

                {/* Avatar / Icon */}
                <Avatar sx={{
                  width: 40, height: 40, zIndex: 1,
                  bgcolor: cfg.bg, color: cfg.color,
                  border: `2px solid #fff`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  fontSize: "0.85rem", fontWeight: 700
                }}>
                  {log.user?.name?.[0]?.toUpperCase() || "?"}
                </Avatar>

                {/* Content Card */}
                <Card elevation={0} sx={{
                  flex: 1, borderRadius: "12px", border: "0.5px solid #f3f4f6",
                  transition: "all 0.2s", "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.04)", borderColor: "#e5e7eb" }
                }}>
                  <CardContent sx={{ p: "16px !important" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#111827" }}>
                            {log.user?.name || "Sistem"}
                          </Typography>
                          <ActionBadge action={log.action} />
                          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", fontWeight: 500 }}>
                            • {formatDate(log.created_at)}
                          </Typography>
                        </Stack>

                        <Typography sx={{ fontSize: "0.85rem", color: "#4b5563", lineHeight: 1.5 }}>
                          {log.action === 'login' ? (
                            <>Melakukan <b>Login</b> ke dalam sistem.</>
                          ) : log.action === 'logout' ? (
                            <>Melakukan <b>Logout</b> dari sistem.</>
                          ) : (
                            <>
                              Menghasilkan aksi <b>{cfg.label}</b> pada modul <b>{log.model_name || log.model_type}</b>
                              {log.model_label && <> dengan identitas: <b style={{ fontFamily: 'monospace', color: '#111827' }}>{log.model_label}</b></>}
                            </>
                          )}
                        </Typography>

                        <Stack direction="row" spacing={2} mt={1.5} alignItems="center">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#cbd5e1" }} />
                            <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af", fontFamily: "monospace" }}>IP: {log.ip_address}</Typography>
                          </Box>
                          {log.action === 'updated' && (
                            <Button
                              size="small"
                              onClick={() => setDetail(log)}
                              sx={{
                                textTransform: "none", fontSize: "0.7rem", fontWeight: 700,
                                p: "2px 8px", minWidth: 0, color: "#2563eb",
                                bgcolor: "#eff6ff", "&:hover": { bgcolor: "#dbeafe" }
                              }}
                            >
                              Lihat Detail Perubahan
                            </Button>
                          )}
                        </Stack>
                      </Box>

                      <Tooltip title="Informasi Teknis">
                        <IconButton size="small" onClick={() => setDetail(log)} sx={{ color: "#d1d5db" }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            );
          })
        )}

        {/* Pagination Simple */}
        {logs?.last_page > 1 && (
          <Stack direction="row" justifyContent="center" mt={2}>
            <Button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ textTransform: "none", color: "#6b7280" }}
            >
              Sebelumnya
            </Button>
            <Typography sx={{ alignSelf: "center", mx: 2, fontSize: "0.8rem", color: "#9ca3af" }}>
              Halaman {page} dari {logs.last_page}
            </Typography>
            <Button
              disabled={page === logs.last_page}
              onClick={() => setPage(page + 1)}
              sx={{ textTransform: "none", color: "#2563eb", fontWeight: 700 }}
            >
              Selanjutnya
            </Button>
          </Stack>
        )}
      </Box>

      {/* ── Detail Dialog ──────────────────────────────────────────────── */}
      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", border: "0.5px solid #f3f4f6", boxShadow: "0 20px 60px rgba(0,0,0,0.1)" },
        }}
      >
        {detail && (
          <>
            <DialogTitle sx={{ pb: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: "8px", bgcolor: "#eff6ff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: "#2563eb" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#111827", lineHeight: 1 }}>
                    Detail Log #{detail.id}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.3 }}>
                    {formatDate(detail.created_at)}
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: "#f3f4f6" }}>
              {/* Meta */}
              <Grid container spacing={2} mb={2}>
                {[
                  { label: "Waktu",      value: formatDate(detail.created_at) },
                  { label: "User",       value: detail.user?.name || "—" },
                  { label: "IP Address", value: detail.ip_address || "—", mono: true },
                ].map((m) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={m.label}>
                    <Typography sx={labelSx}>{m.label}</Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: "#374151", fontFamily: m.mono ? "monospace" : "inherit" }}>
                      {m.value}
                    </Typography>
                  </Grid>
                ))}
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography sx={labelSx}>Aksi</Typography>
                  <ActionBadge action={detail.action} />
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Typography sx={labelSx}>Modul & ID</Typography>
                  <Typography sx={{ fontSize: "0.82rem", color: "#374151" }}>
                    {detail.model_name || detail.model_type} #{detail.model_id}
                    {detail.model_label && (
                      <Box component="span" sx={{ color: "#9ca3af" }}> — {detail.model_label}</Box>
                    )}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ borderColor: "#f3f4f6", mb: 2 }} />

              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#374151", mb: 1 }}>
                Perubahan Data
              </Typography>
              <DiffViewer oldValues={detail.old_values} newValues={detail.new_values} />
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
              <Button onClick={() => setDetail(null)}
                sx={{
                  borderRadius: "9px", fontSize: "0.8rem", textTransform: "none",
                  color: "#6b7280", border: "0.5px solid #e5e7eb",
                  "&:hover": { bgcolor: "#f9fafb" },
                }}>
                Tutup
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
